import React, { Fragment, useEffect, useReducer, useState } from "react";
import EmissionTarget from "./AdvancedTargets/AdvancedEmissionTarget";
import EmissionMetric from "./EmissionMetric";
import { toastMessage } from "../../helper";
import {
  getPcmMappings,
  targetUOMService,
} from "../../services/emissionService";
import { fetchBusinessUnits } from "../../services/orgService";
import {
  createTargetService,
  updateTargetService,
} from "../../services/targetService";
import { useNavigate } from "react-router-dom";
import { deepClone } from "@mui/x-data-grid/utils/utils";
import {
  CATEGORY_CODE,
  CURRENCY,
  METRIC_TYPES,
  STEPS,
  TARGET_REDUCER_TYPES,
  TARGET_TYPES,
  UOM,
} from "../../constants/enums";
import { useTranslation } from "react-i18next";

const removeUndefinedFields = (obj: any) => {
  for (const key in obj) {
    if (obj[key] === undefined || obj[key] === false) {
      delete obj[key];
    }
    if (typeof obj[key] === "object") {
      obj[key] = removeUndefinedFields(obj[key]);
    }
  }
  return obj;
};

const TargetHome = () => {
  const { t, i18n } = useTranslation();

  enum Metrics {
    ACTIVE_EMPLOYEES,
    GROSS_FLOOR_AREA,
    ECONOMIC_INTENSITY,
    TARGET_NO_OF_UNITS,
  }
  const [step, setStep] = useState(STEPS.EMISSION_TARGET);
  const [categoriesByYears, setCategoriesByYears] = useState<Map<string, any>>(
    new Map()
  );
  const [uomMap, setUOMMap] = useState<any>({
    "Active employees": { uoms: [], uom: "", uomSymbol: "" },
    "Gross floor area": { uoms: [], uom: "", uomSymbol: "" },
    "Economic intensity": { uoms: [], uom: "", uomSymbol: "" },
    "Target no. of units": { uoms: [], uom: "", uomSymbol: "" },
  });

  const defaultMetrics: any = [
    {
      type: METRIC_TYPES.ACTIVE_EMPLOYEES,
      value: "",
      uom: UOM.UOM_65,
      uomSymbol: "t CO2e/Employee",
    },
    {
      type: METRIC_TYPES.GROSS_FLOOR_AREA,
      value: "",
    },
    {
      type: METRIC_TYPES.ECONOMIC_INTENSITY,
      // uom: uomMap["Economic intensity"].uoms[0]?.id,
      // uomSymbol: uomMap["Economic intensity"].uoms[0]?.symbol,
      value: "",
    },
    {
      type: METRIC_TYPES.TARGET_NO_OF_UNITS,
      // uom: uomMap["Target No of units"].uoms[0]?.id,
      // uomSymbol: uomMap["Target No of units"].uoms[0]?.symbol,
      value: "",
    },
  ];

  const metricsAttributes = {
    "Active employees": "ACTIVE_EMPLOYEES",
    "Gross floor area": "GROSS_FLOOR_AREA",
    "Economic intensity": "ECONOMIC_INTENSITY",
    "Target no. of units": "TARGET_NO_OF_UNITS",
  } as const;

  const metricsInverseAttributes: any = {
    ACTIVE_EMPLOYEES: "Active employees",
    GROSS_FLOOR_AREA: "Gross floor area",
    ANNUAL_REVENUE: "Annual revenue",
    ECONOMIC_INTENSITY: "Economic intensity",
    TARGET_NO_OF_UNITS: "Target no. of units",
  };

  type MetricsKeys = keyof typeof metricsAttributes;

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [targetCoverage, setTargetCoverage] = useState("");
  const [targetAlignment, setTargetAlignment] = useState("");
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [isMetricsAdded, setIsMetricsAdded] = useState(false);
  // type SwitchStates = {
  //   [type: string]: {
  //     [key: number]: boolean;
  //   };
  // };
  // const [switchStates, setSwitchStates] = useState<SwitchStates>({});

  const initialState: any = {
    error: { targetValid: false, metricsValid: false },
    targets: [
      {
        type: TARGET_TYPES.MOST_RECENT,
        value: 0,
        metrics: [],
        emissions: [],
        attachments: [],
        comment: "",
      },
      {
        type: TARGET_TYPES.NET_ZERO,
        value: 0,
        metrics: [],
        emissions: [],
        attachments: [],
        comment: "",
      },
    ],
    businessUnit: null,
    businessUnitName: null,
    carbonPrice: "",
    currency: CURRENCY.CURRENCY_49,
    currencyName: "USD",
  };
  const [bunitList, setBunitsList] = useState([]);
  const navigate = useNavigate();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [baseValues, setBaseValues] = useState([] as any);
  const [recentValues, setRecentValues] = useState([] as any);
  const [targetValues, setTargetValues] = useState([] as any);
  const [netValues, setNetValues] = useState([] as any);

  interface ScopeMapping {
    [key: number]: {
      [key: number]: string;
    };
  }

  const scopeMapping: ScopeMapping = {
    2: { 0: "1", 1: "2", 2: "3" },
    1: { 0: "1", 1: "3" },
  };

  const getScope = (
    targetCoverageIndex: number,
    rowIdx: number
  ): number | undefined => {
    return scopeMapping[targetCoverageIndex >= 2 ? 2 : targetCoverageIndex]?.[
      rowIdx
    ]
      ? parseInt(
          scopeMapping[targetCoverageIndex >= 2 ? 2 : targetCoverageIndex][
            rowIdx
          ]
        )
      : undefined;
  };

  const findScopeByCategory = (code: CATEGORY_CODE) => {
    if (
      code >= CATEGORY_CODE.CATEGORY_1_1 &&
      code < CATEGORY_CODE.CATEGORY_2_1
    ) {
      return 1;
    } else if (
      CATEGORY_CODE.CATEGORY_2_1 &&
      code < CATEGORY_CODE.CATEGORY_3_1
    ) {
      return 2;
    } else {
      return 3;
    }
  };

  const createKey = (
    year: number,
    scope: number,
    categoryId: number
  ): string => {
    return `${year}-${scope}-${categoryId}`;
  };

  const targetReducer = (targetState: any, action: any) => {
    let targState = targetState;

    switch (action.type) {
      case TARGET_REDUCER_TYPES.ADD_BASELINE: {
        let {
          year,
          isUpdated,
          comment,
          attachments,
          targetCoverageIndex,
          rowIdx,
          scopeNum,
          categoryId,
          id,
        } = action.payload;
        // const isUpdated = isUpdate ? isUpdatePayload : false;
        // if (
        //   targState.targets.findIndex(
        //     (eachTarget: any) => eachTarget.year === year
        //   ) > -1
        // ) {
        //   toastMessage(
        //     "Baseline1 already exists for the particular year",
        //     "error"
        //   );
        // } else {
        const existingBaseline = targetState.targets.find(
          (target: { type: TARGET_TYPES; rowIdx: any; categoryId: any }) => {
            if (targetCoverageIndex < 3) {
              return (
                target.type === TARGET_TYPES.BASELINE &&
                target.rowIdx === rowIdx
              );
            } else {
              return (
                target.type === TARGET_TYPES.BASELINE &&
                target.categoryId === categoryId &&
                target.rowIdx === rowIdx
              );
            }
          }
        );

        if (existingBaseline) {
          existingBaseline.year = year;
        } else {
          const baseline = {
            year,
            type: TARGET_TYPES.BASELINE,
            metrics: [],
            id: isUpdated ? id : false,
            comment,
            attachments,
            targetCoverageIndex,
            rowIdx,
            scope: scopeNum
              ? scopeNum
              : targetCoverageIndex > 0
              ? getScope(targetCoverageIndex, rowIdx)
              : 1,
            ...(targetCoverageIndex >= 3 && {
              categoryId,
              included: true,
            }),
          };

          targState.targets.push(baseline);
        }
        // }

        targetState.error.targetValid =
          targState.targets.every((eachTarget: any) => eachTarget.year) &&
          targetState.businessUnit;

        console.log(targetState, "targetState");
        return targState;
      }

      case TARGET_REDUCER_TYPES.ADD_TARGET: {
        let { targetCoverageIndex, rowIdx, id, categoryId, comment, scopeNum } =
          action.payload;
        let isUpdated = isUpdate ? action.payload.isUpdated : false;
        let targetYear = action.payload.year;
        if (
          targState.targets.findIndex(
            (eachTarget: any) =>
              (eachTarget.type !== TARGET_TYPES.TARGET ||
                (eachTarget.type === TARGET_TYPES.TARGET &&
                  eachTarget?.rowIdx === rowIdx &&
                  eachTarget?.categoryId === categoryId)) &&
              eachTarget.year === targetYear
          ) > -1
        ) {
          toastMessage(
            "Target already exists for the particular year",
            "error"
          );
        } else {
          const currBaseline = targState.targets.find(
            (target: any) =>
              target.type === TARGET_TYPES.BASELINE &&
              target.rowIdx === rowIdx &&
              (selectedIndex !== null && selectedIndex >= 3
                ? target.categoryId === categoryId
                : true)
          );
          let metricArray: any = [];
          currBaseline?.metrics?.forEach((metric: any) => {
            let metricObj = {
              intensity: action.payload.emissionValue / metric.value,
              isCustom: metric.isCustom,
              isUpdated: metric.isUpdated,
              percent: action.payload.percentReduction,
              type: metric.type,
              uom: metric.uom,
              uomSymbol: metric.uomSymbol,
              value: metric.value,
            };
            metricArray.push(metricObj);
          });
          let target = {
            year: targetYear,
            value: action.payload.emissionValue,
            reductionPercentage: action.payload.percentReduction,
            type: TARGET_TYPES.TARGET,
            metrics: currBaseline?.metrics ? metricArray : [],
            id: isUpdate ? action.payload.id : false,
            comment: comment,
            attachments: action.payload.attachments,
            isSBTiAligned: null,
            targetCoverageIndex,
            rowIdx,
            scope: scopeNum
              ? scopeNum
              : targetCoverageIndex > 0
              ? getScope(targetCoverageIndex, rowIdx)
              : 1,
            ...(targetCoverageIndex >= 3 && {
              categoryId,
            }),
          };
          if (action.payload.isSBTiAligned != null) {
            target.isSBTiAligned = action.payload.isSBTiAligned;
          }
          targState.targets.push(target);
          targetState.error.targetValid =
            targState.targets.every((eachTarget: any) => eachTarget.year) &&
            targetState.businessUnit;
          targState.targets.sort((a: any, b: any) => a.target - b.target);
          targState = {
            ...targState,
            targets: [...targState.targets],
            isUpdated: isUpdated,
          };
        }
        console.log(targState);

        return targState;
      }

      case TARGET_REDUCER_TYPES.UPDATE_TARGET: {
        let {
          type,
          year,
          value,
          isUpdated,
          id,
          clearAll,
          prevYear,
          currYear,
          percentage,
          valueUpdated,
          isSBTiAligned,
          comment,
          attachments,
          valueEdit,
          scope,
          categoryIncluded,
          categoryId,
          emissions,
        } = action.payload;
        let currTarget;

        if (type === TARGET_TYPES.MOST_RECENT) {
          currTarget = targState.targets.find(
            (eachTarget: any) => eachTarget.type === TARGET_TYPES.MOST_RECENT
          );

          if (!isNaN(value)) {
            currTarget.value = value;
            currTarget.isUpdated = isUpdate ? isUpdated : false;
            if (!currTarget.id) {
              currTarget.id = isUpdate ? action.payload.id : false;
            }
          }
          if (year) {
            currTarget.year = year;
            currTarget.isUpdated = isUpdate ? isUpdated : false;
            if (!currTarget.id) {
              currTarget.id = isUpdate ? action.payload.id : false;
            }
          }
          if (comment != null) {
            currTarget.comment = comment;
          }
          if (attachments) {
            currTarget.attachments = attachments.length > 0 ? attachments : [];
          }
          if (emissions) {
            currTarget.emissions = emissions;
          }
        } else if (type === TARGET_TYPES.NET_ZERO) {
          currTarget = targState.targets.find(
            (eachTarget: any) => eachTarget.type === TARGET_TYPES.NET_ZERO
          );
          if (year) {
            if (
              targState.targets.findIndex(
                (eachTarget: any) => eachTarget?.year === year
              ) > -1
            ) {
              toastMessage(
                "Target already exists for the particular year",
                "error"
              );
            } else {
              currTarget.year = year;
              currTarget.isUpdated = isUpdate ? isUpdated : false;
              if (!currTarget.id) {
                currTarget.id = isUpdate ? action.payload.id : false;
              }
            }
          }
          if (comment != null) {
            currTarget.comment = comment;
          }
          if (attachments) {
            currTarget.attachments = attachments.length > 0 ? attachments : [];
          }
          if (emissions) {
            currTarget.emissions = emissions;
          }
        } else if (type === TARGET_TYPES.BASELINE) {
          if (!isNaN(prevYear) && prevYear > 0 && !isNaN(year)) {
            currTarget = targState.targets.find(
              (eachBaseline: any) =>
                eachBaseline?.year === prevYear &&
                eachBaseline.scope === scope &&
                (selectedIndex !== null && selectedIndex < 3
                  ? true
                  : eachBaseline?.categoryId === categoryId)
            );
            if (
              targState.targets.findIndex(
                (eachBaseline: any) =>
                  eachBaseline.year === year &&
                  eachBaseline.scope === scope &&
                  (selectedIndex !== null && selectedIndex < 3
                    ? true
                    : eachBaseline?.categoryId === categoryId) //&& eachBaseline.value === value
              ) > -1
            ) {
              toastMessage(
                "Target already exists for the particular year",
                "error"
              );
            } else {
              currTarget.year = year;
              // currTarget.value = value;
              currTarget.isUpdated = isUpdate ? isUpdated : false;
              if (!currTarget.id) {
                currTarget.id = isUpdate ? action.payload.id : false;
              }
            }
          } else {
            currTarget = targState.targets.find(
              (eachBaseline: any) =>
                eachBaseline?.year === year &&
                eachBaseline.scope === scope &&
                (selectedIndex !== null && selectedIndex < 3
                  ? true
                  : eachBaseline?.categoryId === categoryId)
            );

            if (!valueUpdated) {
              currTarget.included = categoryIncluded;
              if (comment != null) {
                currTarget.comment = comment;
              }
              if (attachments) {
                currTarget.attachments =
                  attachments.length > 0 ? attachments : [];
              }
            } else {
              currTarget.value = value;
              currTarget.isUpdated = isUpdate ? isUpdated : false;
            }
          }
        } else {
          if (!isNaN(prevYear) && prevYear > 0 && !isNaN(currYear)) {
            currTarget = targState.targets.find(
              (eachTarget: any) =>
                eachTarget?.year === prevYear &&
                eachTarget.scope === scope &&
                (selectedIndex !== null && selectedIndex < 3
                  ? true
                  : eachTarget?.categoryId === categoryId)
            );
            if (
              targState.targets.findIndex(
                (eachTarget: any) =>
                  eachTarget.year === year &&
                  eachTarget.scope === scope &&
                  (selectedIndex !== null && selectedIndex < 3
                    ? true
                    : eachTarget?.categoryId === categoryId) //&& eachBaseline.value === value
              ) > -1
            ) {
              toastMessage(
                "Target already exists for the particular year",
                "error"
              );
            } else {
              currTarget.year = currYear;
              currTarget.value = parseFloat(value);
              currTarget.reductionPercentage = percentage;
              currTarget.isUpdated = isUpdate ? isUpdated : false;
              if (isSBTiAligned != null) {
                currTarget.isSBTiAligned = isSBTiAligned;
              }
              currTarget.reductionPercentage = percentage;
              currTarget.value = parseFloat(value);
              if (isSBTiAligned != null) {
                currTarget.isSBTiAligned = isSBTiAligned;
              }
            }
          } else {
            currTarget = targState.targets.find(
              (eachTarget: any) =>
                eachTarget?.year === prevYear &&
                eachTarget.scope === scope &&
                (selectedIndex !== null && selectedIndex < 3
                  ? true
                  : eachTarget?.categoryId === categoryId)
            );

            if (!isNaN(parseFloat(value)) && valueUpdated) {
              currTarget.value = parseFloat(value);
              currTarget.reductionPercentage = percentage;
              currTarget.isUpdated = isUpdate ? isUpdated : false;
              if (isSBTiAligned != null) {
                currTarget.isSBTiAligned = isSBTiAligned;
              }

              if (!currTarget.id) {
                currTarget.id = isUpdate ? action.payload.id : false;
              }
            }
            if (!isNaN(prevYear) && !isNaN(percentage) && valueUpdated) {
              currTarget.reductionPercentage = percentage;
              currTarget.value = parseFloat(value);
              if (isSBTiAligned != null) {
                currTarget.isSBTiAligned = isSBTiAligned;
              }
            }
            if (!isNaN(prevYear) && comment !== "" && !valueUpdated) {
              currTarget.comment = comment;
            }
            if (!isNaN(prevYear) && attachments && !valueUpdated) {
              currTarget.attachments =
                attachments.length > 0 ? attachments : [];
            }
          }
        }

        targetState.error.targetValid =
          targState.targets.every(
            (eachTarget: any) => eachTarget.year && eachTarget.type
          ) &&
          targetState.businessUnit &&
          (targState.targets?.length ?? 0) > 3;
        targState.targets.sort((a: any, b: any) => a.year - b.year);
        targState = { ...targState, targets: [...targState.targets] };

        return targState;
      }
      case TARGET_REDUCER_TYPES.UPDATE_TARGET_METRIC: {
        let {
          type,
          year,
          categoryId,
          value,
          percent,
          isUpdated,
          id,
          baseLineYear,
          intensity,
          scope,
        } = action.payload;
        let currTarget: any = targState.targets.find(
          (eachTarget: any) =>
            eachTarget.year === year &&
            eachTarget.scope === scope &&
            (selectedIndex !== null && selectedIndex < 3
              ? true
              : eachTarget?.categoryId === categoryId)
        );
        if (currTarget?.type === TARGET_TYPES.BASELINE) {
          let baseLineMetric = currTarget.metrics.find(
            (eachMetric: any) => eachMetric.type === type
          );
          if (id) {
            baseLineMetric.id = id;
            baseLineMetric.value = !isNaN(value) ? value : "";
            baseLineMetric.intensity = !isNaN(intensity) ? intensity : "";
            baseLineMetric.percent = !isNaN(percent) ? percent : "";
          } else {
            baseLineMetric.isUpdated = isUpdate ? isUpdated : false;
            baseLineMetric.value = !isNaN(value) ? value : "";
            isUpdate && id != null && (baseLineMetric.id = id);
            baseLineMetric.intensity =
              parseFloat(value) === 0 ||
              isNaN(value) ||
              isNaN(currTarget.value / parseFloat(value))
                ? "0"
                : (currTarget.value / parseFloat(value)).toFixed(2);

            targState.targets
              .filter(
                (eachTarget: any) =>
                  eachTarget.type === TARGET_TYPES.TARGET &&
                  eachTarget.scope === currTarget.scope &&
                  (selectedIndex !== null && selectedIndex >= 3
                    ? eachTarget?.categoryId === categoryId ||
                      eachTarget?.categoryId === -1
                    : true)
              )
              .forEach((eachTarget: any) => {
                let currMetric = eachTarget?.metrics?.find(
                  (eachMetric: any) => eachMetric.type === type
                );
                currMetric.isUpdated = isUpdate ? isUpdated : false;
                isUpdate && id != null && (currMetric.id = id);
                if (!isNaN(value) && percent === undefined) {
                  // let currentMetricValue = !isNaN(value) ? parseFloat(value) : 0;
                  currMetric.value = !isNaN(value) ? value : "0";
                  currMetric.intensity = eachTarget.value / currMetric.value;
                  currMetric.percent =
                    value !== "" &&
                    !isNaN(parseFloat(baseLineMetric?.intensity)) &&
                    parseFloat(baseLineMetric?.intensity) !== 0
                      ? (
                          ((baseLineMetric?.intensity - currMetric.intensity) /
                            baseLineMetric?.intensity) *
                          100
                        ).toFixed(2)
                      : "0";
                } else if (value === undefined && !isNaN(percent)) {
                  // let currentMetricPercent = !isNaN(parseFloat(percent))
                  //   ? parseFloat(percent)
                  //   : 0;
                  currMetric.percent = !isNaN(percent) ? percent : "0";
                  currMetric.value = !isNaN(baseLineMetric?.intensity)
                    ? (parseFloat(baseLineMetric?.intensity) *
                        currMetric.percent) /
                      100
                    : 0;
                  currMetric.value = currMetric?.value.toFixed(2);
                }
              });
          }
        } else if (currTarget?.type === TARGET_TYPES.TARGET) {
          let currBaseLineTarget: any = targState.targets.find(
            (eachTarget: any) => eachTarget.year === baseLineYear
          );
          let baseLineCurrMetric = currBaseLineTarget?.metrics.find(
            (eachMetric: any) => eachMetric.type === type
          );
          let currMetric = currTarget?.metrics.find(
            (eachMetric: any) => eachMetric.type === type
          );
          if (currMetric) {
            currMetric.isUpdated = isUpdate ? isUpdated : false;
          }
          isUpdate && id != null && currMetric && (currMetric.id = id);
          if (id) {
            currMetric.id = id;
            currMetric.value = !isNaN(value) ? value : "";
            currMetric.intensity = !isNaN(intensity) ? intensity : "";
            currMetric.percent = !isNaN(percent) ? percent : "";
          } else {
            if (!isNaN(value) && percent === undefined) {
              currMetric && (currMetric.value = !isNaN(value) ? value : "0");
              currMetric.intensity = currTarget.value / currMetric.value;
              currMetric &&
                (currMetric.percent =
                  value !== "" &&
                  !isNaN(parseFloat(currMetric?.intensity)) &&
                  parseFloat(currMetric?.intensity) !== 0
                    ? (
                        ((baseLineCurrMetric.intensity - currMetric.intensity) /
                          baseLineCurrMetric.intensity) *
                        100
                      ).toFixed(2)
                    : "0");
            } else if (value === undefined && !isNaN(percent)) {
              currMetric.percent = !isNaN(percent) ? percent : "0";
              currMetric.intensity =
                (baseLineCurrMetric.intensity * (100 - currMetric.percent)) /
                100;
            } else if (
              value === undefined &&
              percent === undefined &&
              !isNaN(intensity)
            ) {
              currMetric.intensity = intensity;
              currMetric.percent = (
                ((baseLineCurrMetric.intensity - currMetric.intensity) /
                  baseLineCurrMetric.intensity) *
                100
              ).toFixed(2);
            }
          }
        } else {
          let currMetric = currTarget?.metrics.find(
            (eachMetric: any) => eachMetric.type === type
          );
          if (currMetric) {
            currMetric.isUpdated = isUpdate ? isUpdated : false;
          }
          if (!isNaN(value)) {
            currMetric && (currMetric.value = !isNaN(value) ? value : "0");
          }
        }
        console.log(targState);

        return { ...targState };
      }
      case TARGET_REDUCER_TYPES.REMOVE_TARGET: {
        let targetYear = action.payload.year;
        let isDeleted = isUpdate ? action.payload.isDeleted : false;
        const tempDeletedTargets = targState.targets.map((eachTarget: any) => {
          if (eachTarget.year === targetYear) {
            return eachTarget.id;
          }
        });
        let deletedTargets: any = [];
        let newDeleted = tempDeletedTargets.filter((each: any) => !!each);
        if (targState.deletedTargets) {
          targState.deletedTargets.forEach((each: any) => {
            deletedTargets.push(each);
          });
        }
        newDeleted.forEach((each: any) => {
          deletedTargets.push(each);
        });
        let targets = targState.targets.filter(
          (eachTarget: any) => eachTarget.year !== targetYear
        );
        targState = {
          ...targState,
          error: {
            ...targState.error,
          },
          targets: targets,
          isDeleted: isDeleted,
          deletedTargets: deletedTargets,
        };
        // targState.error.targetValid =
        //   targState.targets.length > 2 ? true : false;
        return targState;
      }
      case TARGET_REDUCER_TYPES.SELECT_FACILITY: {
        const isUpdated = action.payload.isUpdated;
        let targetsCopy = targState.targets;
        if (isUpdate && isUpdated) {
          targetsCopy = targState.targets.map((eachTarget: any) => {
            return {
              ...eachTarget,
              isUpdated: isUpdated,
            };
          });
        }
        targState = {
          ...targState,
          ...action.payload,
          targets: targetsCopy,
        };
        delete targState["isUpdated"];
        return targState;
      }

      case TARGET_REDUCER_TYPES.CARBON_PRICE: {
        const isUpdated = action.payload.isUpdated;
        let targetsCopy = targState.targets;
        if (isUpdate && isUpdated) {
          targetsCopy = targState.targets.map((eachTarget: any) => {
            return {
              ...eachTarget,
              isUpdated: isUpdated,
            };
          });
        }
        targState = {
          ...targState,
          ...action.payload,
          targets: targetsCopy,
        };
        delete targState["isUpdated"];
        return targState;
      }
      case TARGET_REDUCER_TYPES.SET_CARBON_CURRENCY: {
        targState = { ...targState, ...action.payload };
        console.log("targetstate", targState);
        return targState;
      }

      case TARGET_REDUCER_TYPES.ADD_METRICS: {
        setIsMetricsAdded(true);
        let { rowIdx, categoryId, netZeroId } = action.payload.metrics[0];
        const typeVal = action.payload.metrics[0].type || "";

        targState.targets.forEach((eachTarget: any) => {
          if (eachTarget.type === TARGET_TYPES.NET_ZERO) {
            targState.targets.forEach((baselineTarget: any) => {
              if (baselineTarget.type === TARGET_TYPES.BASELINE) {
                baselineTarget.metrics.forEach((baselineMetric: any) => {
                  const metricExists = eachTarget.metrics.some(
                    (netzeroMetric: any) =>
                      netzeroMetric.type === baselineMetric.type
                  );
                  if (!metricExists) {
                    const newMetric = { ...baselineMetric };
                    newMetric.value = 0;
                    delete newMetric.intensity;
                    delete newMetric.percent;
                    if (netZeroId) {
                      newMetric.id = netZeroId;
                    }
                    eachTarget.metrics.push(newMetric);
                  }
                });
              }
            });
          } else {
            if (
              eachTarget.rowIdx === rowIdx &&
              (selectedIndex !== null && selectedIndex < 3
                ? true
                : eachTarget.categoryId === categoryId)
            ) {
              const newMetric = {
                type: metricsAttributes[typeVal as MetricsKeys] || typeVal,
                value: "",
                uom: action.payload.metrics[0].uom,
                uomSymbol: action.payload.metrics[0].uomSymbol,
                intensity: "",
                percent: "",
                isCustom: action.payload.metrics[0].isCustom,
              };
              eachTarget.metrics.unshift(newMetric);
            }
          }
        });
        return { ...targState };
      }

      case TARGET_REDUCER_TYPES.REMOVE_METRICS: {
        const { rowIdx, type } = action.payload.metrics[0];

        const updatedTargets = targState.targets.map((eachTarget: any) => {
          if (eachTarget.rowIdx === rowIdx) {
            return {
              ...eachTarget,
              metrics: eachTarget.metrics.filter(
                (metric: any) => metricsInverseAttributes[metric.type] !== type
              ),
            };
          }
          return eachTarget;
        });

        const updatedState = {
          ...targState,
          targets: updatedTargets,
        };

        return updatedState;
      }

      case TARGET_REDUCER_TYPES.ADD_EMISSIONS: {
        targetState.targets.forEach((eachTarget: any) => {
          const key = createKey(
            eachTarget.year,
            eachTarget.scope || 0,
            eachTarget?.categoryId || 0
          );

          const targetCategories = categoriesByYears.get(key);

          eachTarget.emissions = [];

          targetCategories?.forEach(
            (eachCat: {
              value: number;
              sbtVal: number;
              id: number;
              category: number;
              attachments: any;
              comments: string;
              included: boolean;
              code: CATEGORY_CODE;
            }) => {
              const id = eachCat.category && eachCat.id ? eachCat.id : null;
              const value = eachCat.value || 0;
              const reductionPercentage = eachCat?.sbtVal || 0;
              const comments = eachCat.comments || "";
              const attachments = eachCat.attachments || [];
              const isIncluded = eachCat.included;
              const finalValue = isIncluded ? value : 0;

              const emission: any = {
                value: finalValue,
                reductionPercentage: reductionPercentage,
                category:
                  eachCat.category && eachCat.id
                    ? eachCat.category
                    : eachCat.id,
                attachments: attachments,
                comments: comments,
                isIncluded: isIncluded,
                scope: findScopeByCategory(eachCat.code),
              };
              if (id !== null) {
                emission.id = id;
              }
              eachTarget.emissions.push(emission);
            }
          );
        });

        targetState.targets.forEach((eachTarget: any) => {
          if (eachTarget.type === TARGET_TYPES.NET_ZERO) {
            targetState.targets.forEach((target: any) => {
              if (target.type === TARGET_TYPES.TARGET) {
                target.emissions.forEach((emission: any) => {
                  const isCategoryPresent = eachTarget.emissions.some(
                    (each: any) => each.category === emission.category
                  );
                  if (!isCategoryPresent) {
                    eachTarget.emissions.push({
                      value: 0,
                      category: emission.category,
                      scope: emission.scope,
                      isIncluded: emission.isIncluded,
                    });
                  }
                });
              }
            });
          }
        });

        console.log(targetState);
        return { ...targetState };
      }

      case TARGET_REDUCER_TYPES.RESET_STATE: {
        return {
          ...targState,
          targets: [
            {
              type: TARGET_TYPES.MOST_RECENT,
              value: 0,
              metrics: [],
              emissions: [],
              attachments: [],
              comment: "",
            },
            {
              type: TARGET_TYPES.NET_ZERO,
              value: 0,
              metrics: [],
              emissions: [],
              attachments: [],
              comment: "",
            },
          ],
        };
      }
      case TARGET_REDUCER_TYPES.UPDATE_METRIC_UOMS: {
        let uomMap = action.payload;
        targState.targets.forEach((target: any) => {
          target.metrics.forEach((metric: any) => {
            metric.uom = uomMap[metricsInverseAttributes[metric.type]].uom;
            metric.uomSymbol =
              uomMap[metricsInverseAttributes[metric.type]].uomSymbol;
          });
        });
        return JSON.parse(JSON.stringify(targState));
      }
    }
  };

  const updateUOM = (map: any) => {
    setUOMMap(JSON.parse(JSON.stringify(map)));
    dispatch({ type: TARGET_REDUCER_TYPES.UPDATE_METRIC_UOMS, payload: map });
  };

  const [targetState, dispatch] = useReducer(targetReducer, initialState);
  useEffect(() => {
    fetchBusinessUnits({}).then((res) => {
      const data = res.data;
      const bunitsList: [] = data.businessUnits;
      setBunitsList([...bunitsList]);
    });
  }, []);
  useEffect(() => {
    targetUOMService().then((res: any) => {
      let data = res.data;
      let unitsMap = uomMap;
      unitsMap["Active employees"] = {
        uoms: data.t1,
        uom: data.t1[0].id,
        uomSymbol: data.t1[0].symbol,
      };
      unitsMap["Gross floor area"] = {
        uoms: data.t2,
        uom: data.t2[0].id,
        uomSymbol: data.t2[0].symbol,
      };
      unitsMap["Economic intensity"] = {
        uoms: data.t3,
        uom: data.t3[0].id,
        uomSymbol: data.t3[0].symbol,
      };
      unitsMap["Target no. of units"] = {
        uoms: data.t4,
        uom: data.t4[0].id,
        uomSymbol: data.t4[0].symbol,
      };
      let selectedUom = data.t3.find(
        (eachUom: any) =>
          eachUom?.symbol?.split("/")[1] === targetState?.currencyName
      );
      /*unitsMap["Economic intensity"] = {
        uoms: data.t3,
        uom: selectedUom ? selectedUom.id : data.t3[0].id,
        uomSymbol: selectedUom ? selectedUom.symbol : data.t3[0].symbol,
      };*/

      setUOMMap({ ...unitsMap });
    });
  }, [targetState.currencyName]);
  useEffect(() => {
    if (isSubmit) {
      setIsLoading(true);
      let protocol = localStorage.getItem("protocol");
      getPcmMappings().then((mappingRes) => {
        let mappings = mappingRes.data;
        const targetsCopy = JSON.parse(JSON.stringify(targetState.targets));
        let reqObj = {
          uom: UOM.UOM_43,
          uomName: "t CO2e",
          targetCoverage: targetCoverage,
          targetAlignment: targetAlignment,
          deletedTargets: targetState.deletedTargets || [],
          businessUnit: targetState.businessUnit,
          carbonPrice: targetState.carbonPrice,
          currency: targetState.currency,
          data: targetsCopy.map((eachTarget: any) => {
            eachTarget.metrics = eachTarget.metrics.map((eachMetrics: any) => {
              eachMetrics.value = eachMetrics.value
                ? parseFloat(eachMetrics.value)
                : 0;
              eachMetrics?.intensity &&
                (eachMetrics.intensity = parseFloat(eachMetrics.intensity));
              eachMetrics?.intensity === "" && (eachMetrics.intensity = 0);
              eachMetrics?.percent &&
                (eachMetrics.percent = parseFloat(eachMetrics.percent));
              eachMetrics?.percent === "" && (eachMetrics.percent = 0);

              // if (eachTarget.type === TARGET_TYPES.TARGET) {
              //   eachMetrics.intensity = eachMetrics.value;
              //   delete eachMetrics.value; //TOBEREVIEWED
              // }

              return eachMetrics;
            });

            eachTarget.emissions = eachTarget.emissions.map(
              (eachEmissions: any) => {
                let mapping;
                if (protocol == "ghg") {
                  mapping = mappings.filter(
                    (each: any) => each.ghg == eachEmissions.category
                  );
                } else {
                  mapping = mappings.filter(
                    (each: any) => each.iso == eachEmissions.category
                  );
                }
                eachEmissions.category = mapping[0].iso;
                return eachEmissions;
              }
            );

            if (
              eachTarget.type === TARGET_TYPES.TARGET ||
              eachTarget.type === TARGET_TYPES.BASELINE
            ) {
              if (eachTarget.targetCoverageIndex === 0) {
                delete eachTarget.scope;
              } else if (
                eachTarget.targetCoverageIndex === 1 &&
                eachTarget.rowIdx === 0
              ) {
                delete eachTarget.scope;
              }
              eachTarget.isUpdated = false;
            } else if (eachTarget.type === TARGET_TYPES.BASELINE) {
              //|| eachTarget.type === TARGET_TYPES.TARGET){
              delete eachTarget.uom;
              delete eachTarget.uomName;
              // delete eachTarget.rowIdx;
              // delete eachTarget.targetCoverageIndex;
            } else if (eachTarget.type === TARGET_TYPES.NET_ZERO) {
              eachTarget.value = 0;
            }
            if (!eachTarget.id) {
              delete eachTarget.id;
            }
            return eachTarget;
          }),
        };
        let dataWithIsUpdated = reqObj.data.map((eachObj: any) => {
          let isUpdated = false;
          eachObj.metrics.forEach((eachMetric: any) => {
            isUpdated = isUpdated || eachMetric.isUpdated;
          });
          if (isUpdated) {
            return {
              ...eachObj,
              isUpdated: isUpdated,
            };
          }
          return eachObj;
        });
        // const hasUpdates = dataWithIsUpdated.some(
        //   (eachObj: any) => eachObj.isUpdated
        // );
        // if (hasUpdates) {

        if (isUpdate) {
          reqObj.data = dataWithIsUpdated;
          reqObj = removeUndefinedFields(reqObj);
          updateTargetService(reqObj)
            .then((res: any) => {
              toastMessage("Target updated Successfully", "success");
              navigate("/targets");
            })
            .catch((err) => {
              console.log(err);
              toastMessage(err.response.data.message, "error");
              setIsSubmit(false);
              //navigate("/targets");
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
          createTargetService(reqObj)
            .then((res: any) => {
              toastMessage("Target Created Successfully", "success");
              navigate("/targets");
            })
            .catch((err) => {
              console.log(err);
              toastMessage(err.response.data.message, "error");
              setIsSubmit(false);
              //navigate("/targets");
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
      });
    }
  }, [isSubmit]);
  return (
    <div className="pw-emission-home w-full  flex flex-row grow">
      <div className="pw-creation-side-bar">
        <div
          className={`pw-org-side-elem ${
            step === STEPS.EMISSION_TARGET ? "pw-org-active" : "pw-org-inactive"
          }`}
          onClick={() => {
            setStep(STEPS.EMISSION_TARGET);
          }}
        >
          {t("Emission Targets")}
        </div>
        <div
          className={`pw-org-side-elem ${
            step === STEPS.METRICS ? "pw-org-active" : "pw-org-inactive"
          }`}
          onClick={() => {
            targetState.error.targetValid && setStep(STEPS.METRICS);
          }}
        >
          {t("Metrics")}
        </div>
      </div>
      <div className="overflow-y-auto pw-emission-home-main">
        <div
          className="h-full"
          style={{ display: step === STEPS.EMISSION_TARGET ? "block" : "none" }}
        >
          <EmissionTarget
            setStep={setStep}
            dispatch={dispatch}
            targetState={targetState}
            setBaseValues={setBaseValues}
            setRecentValues={setRecentValues}
            setTargetValues={setTargetValues}
            setNetValues={setNetValues}
            categoryList={categoryList}
            setCategoryList={setCategoryList}
            categoriesByYears={categoriesByYears}
            setCategoriesByYears={setCategoriesByYears}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            targetCoverage={targetCoverage}
            setTargetCoverage={setTargetCoverage}
            setTargetAlignment={setTargetAlignment}
            createKey={createKey}
          />
        </div>
        <div
          className="h-full"
          style={{ display: step === STEPS.METRICS ? "block" : "none" }}
        >
          <EmissionMetric
            setStep={setStep}
            dispatch={dispatch}
            targetState={targetState}
            categoryList={categoryList}
            setIsSubmit={setIsSubmit}
            isLoading={isLoading}
            setIsUpdate={setIsUpdate}
            uomMap={uomMap}
            setUOMMap={updateUOM}
            baseValues={baseValues}
            recentValues={recentValues}
            targetValues={targetValues}
            netValues={netValues}
            selectedIndex={selectedIndex}
            isMetricsAdded={isMetricsAdded}
            setIsMetricsAdded={setIsMetricsAdded}
          />
        </div>
      </div>
    </div>
  );
};

export default TargetHome;
